import React, { useEffect, useState, useCallback } from "react";
import { RefreshCw } from "lucide-react";
import api from "@/lib/api";

/**
 * AppVersionBanner
 *
 * Polls GET /api/version every 60s. The value is a server-start timestamp that
 * bumps every deploy. We remember the first value we saw ("baseline"); as soon
 * as a subsequent poll returns something different, we know a new build has
 * been deployed and show an unmissable red banner at the very top of every
 * page prompting the user to refresh. One tap = clean cache-busting reload.
 *
 * This solves the classic SPA staleness problem where existing customers keep
 * running the old JS bundle they cached until they clear cache or open the
 * site in incognito. No service worker needed — the poll is ~30 bytes/min.
 */
export default function AppVersionBanner() {
  const [baseline, setBaseline] = useState(null);
  const [latest, setLatest] = useState(null);

  const checkOnce = useCallback(async () => {
    try {
      const { data } = await api.get("/version");
      if (!data || !data.version) return;
      // Only capture baseline on the FIRST successful response so a transient
      // 500 during boot doesn't trick us into showing "new version" to
      // literally every user on the site.
      setBaseline((prev) => prev ?? data.version);
      setLatest(data.version);
    } catch (_) {
      // Ignore network blips — silent poll.
    }
  }, []);

  useEffect(() => {
    checkOnce();
    const id = setInterval(checkOnce, 60_000);
    // Also check when the tab becomes visible again after being hidden — this
    // catches the case where a user's laptop was asleep during a deploy.
    const onVis = () => {
      if (document.visibilityState === "visible") checkOnce();
    };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [checkOnce]);

  const needsReload = baseline && latest && baseline !== latest;
  if (!needsReload) return null;

  const doReload = () => {
    // Cache-busting query param forces the browser to bypass any cached
    // index.html; the `?v=` value is meaningless but changes each click so
    // aggressive CDNs / service workers can't serve the stale response.
    try {
      const url = new URL(window.location.href);
      url.searchParams.set("v", String(Date.now()));
      window.location.replace(url.toString());
    } catch (_) {
      window.location.reload();
    }
  };

  return (
    <div
      className="w-full bg-[var(--ia-red)] text-white text-sm border-b-2 border-black"
      role="alert"
      aria-live="polite"
      data-testid="app-version-banner"
    >
      <div className="max-w-7xl mx-auto px-4 py-2 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <RefreshCw className="h-4 w-4 shrink-0" />
          <span className="font-semibold truncate">
            A new version of Infinite Auctions is available.
          </span>
          <span className="hidden sm:inline text-white/80">
            Refresh to get the latest bidding experience.
          </span>
        </div>
        <button
          type="button"
          onClick={doReload}
          className="shrink-0 bg-white text-[var(--ia-red)] font-black tracking-eyebrow uppercase text-[11px] px-3 py-1.5 hover:bg-black hover:text-white transition-colors"
          data-testid="app-version-refresh-btn"
        >
          Refresh now
        </button>
      </div>
    </div>
  );
}
